/**
 * NodeFactory.js
 * 
 * Provides functions to create default node objects and new nodes with user info.
 */

// Firebase
import { Timestamp } from 'firebase/firestore';

// Services
import { getNodeType } from 'services/nodetypes/NodeTypesCacheService';

// Utilities
import { generateKey } from 'utilities/Keys';
import { detectFileNodeType, getResolvedFields } from 'utilities/NodeType';
import { buildUserSummary } from 'utilities/Users';
import { normalizeEnumOptionValues } from 'utilities/EnumOptions';

// Default node structure for new nodes
const defaultNode = {
	key: null,                         // Unique identifier for this node (document ID)
	userKey: null,                     // Creator's user key (set on save)
	workspaceKey: null,                // Workspace key (set when created within a workspace)
	user: {},                          // Creator user summary (can be cached lightweight version)
	type: 'NODE',                      // Type of node: 'PHOTO', 'VIDEO', 'NODE', etc.n
	title: '',                         // Title or name of the node
	index: [],                         // Searchable index fields (substrings of title)
	description: '',                   // Optional detailed description
	tags: [],                          // Searchable tag keywords
	link: null,                        // Permanent or shareable link to this node
	color: null,                       // Optional identity color accent for the node
	iconKey: null,                     // Optional icon override (inherits type iconKey when null)
	coverUrl: null,                    // Cover image URL for the node (if applicable)

	// Upload metadata
	storagePath: null,                 // Firebase Storage path of the main content
	url: null,                         // Primary display URL
	url_feed: null,                    // Optimized URL for feed display
	url_grid: null,                    // Optimized URL for grid display
	url_thumb: null,                   // Optimized thumbnail URL
	base64Data: null,                  // Pre-upload preview for client-side rendering
	localUri: null,                    // Full local path (e.g., file:// on device)
	downloadUrl: null,                 // Direct download URL if needed
	width: null,                       // Width of the original media (if applicable)
	height: null,                      // Height of the original media (if applicable)
	aspectRatio: null,                 // Aspect ratio of the original media (if applicable)

	// Engagement counts (used for ranking, sorting, gamification)
	favoriteCount: 0,
	commentCount: 0,
	likeCount: 0,
	shareCount: 0,
	viewCount: 0,
	referrerCount: 0,

	// Timestamps
	dateCreated: Timestamp.now(),            // When the node was first created (client-generated by default)
	dateModified: Timestamp.now(),           // Last modified (client-generated, can be overridden server-side)
	dateUploaded: null,                 // When the file was successfully uploaded (optional)
};

const DEFAULT_UNSUPPORTED_TYPES = new Set([
	'tags',
	'location',
	'color',
	'icon',
	'checklist',
	'dates',
]);

const hasMeaningfulDefault = (fieldType, value) => {
	if (value === undefined || value === null) return false;
	if (fieldType !== 'boolean' && value === '') return false;
	return true;
};

const normalizeDefaultForField = (field, rawDefault) => {
	const fieldType = field?.type;

	if (!hasMeaningfulDefault(fieldType, rawDefault)) {
		return { hasValue: false };
	}

	if (DEFAULT_UNSUPPORTED_TYPES.has(fieldType)) {
		return { hasValue: false };
	}

	if (fieldType === 'enum') {
		const optionValues = normalizeEnumOptionValues(field?.options);
		const value = String(rawDefault);
		if (!optionValues.length || !optionValues.includes(value)) {
			return { hasValue: false };
		}
		return { hasValue: true, value };
	}

	if (fieldType === 'boolean') {
		return { hasValue: true, value: !!rawDefault };
	}

	if (fieldType === 'number') {
		const parsed = Number(rawDefault);
		if (!Number.isFinite(parsed)) return { hasValue: false };
		return { hasValue: true, value: parsed };
	}

	if (fieldType === 'percentage') {
		const parsed = Number(rawDefault);
		if (!Number.isFinite(parsed)) return { hasValue: false };
		const normalized = Math.max(0, Math.min(100, Math.round(parsed)));
		return { hasValue: true, value: normalized };
	}

	return { hasValue: true, value: rawDefault };
};

const getSectionKeyForType = (typeKey) => {
	const sourceDef = getNodeType(typeKey);
	if (typeof sourceDef?.dataSection === 'string' && sourceDef.dataSection.trim()) {
		return sourceDef.dataSection.trim();
	}

	const normalized = String(typeKey || '').trim().toLowerCase();
	return normalized ? `data_${normalized}` : null;
};

const applySchemaFieldDefaults = (node, nodeTypeKey) => {
	if (!node || !nodeTypeKey) return;

	const fields = getResolvedFields(nodeTypeKey, { includeHidden: true });
	if (!Array.isArray(fields) || fields.length === 0) return;

	for (const field of fields) {
		if (!field?.name) continue;

		const normalized = normalizeDefaultForField(field, field.default);
		if (!normalized.hasValue) continue;

		const sourceType = String(field.source || nodeTypeKey).trim();
		const sectionKey = getSectionKeyForType(sourceType);
		if (!sectionKey) continue;

		const currentSection = node?.[sectionKey];
		const nextSection = (currentSection && typeof currentSection === 'object')
			? { ...currentSection }
			: {};

		nextSection[field.name] = normalized.value;
		node[sectionKey] = nextSection;
	}
};

/**
 * Returns a new node object with default values, customized by type.
 * 
 * @param {string} nodeTypeKey - The type of node to create (e.g. "PHOTO")
 * @param {string|null} workspaceKey - Optional space key to associate with the node
 * 
 * @returns {object} The new node object with defaults
 */
export async function getDefaultNode(nodeTypeKey = 'NODE', workspaceKey = null) {

    // Get node type definition
	const def = getNodeType(nodeTypeKey);

    // Determine resolved type key
	const resolvedTypeKey = def?.key || nodeTypeKey || 'NODE';

	// Return new node object
	const next = {
		...defaultNode,
		key: generateKey(),
		type: resolvedTypeKey,
	};

	// Set workspaceKey if provided
	if (typeof workspaceKey === 'string' && workspaceKey.trim()) {
		next.workspaceKey = workspaceKey.trim();
	}

	return next;
}

/**
 * Creates a new node object with defaults, user info, and timestamps.
 * 
 * @param {string} nodeTypeKey - The type of node to create (e.g. "PHOTO")
 * @param {object} user - The user object creating the node
 * @param {string|null} workspaceKey - Optional space key to associate with the node
 * 
 * @return {object} The new node object.
 */
export async function createNode(nodeTypeKey, user, workspaceKey = null) {

    // Get node type definition
	const def = getNodeType(nodeTypeKey);

    // Ensure valid type
	if (!def) throw new Error(`Unknown nodeType: ${nodeTypeKey}`);

    // Generate unique key
	const key = generateKey();

    // Get base default node
	const base = await getDefaultNode(def.key);

    // Get type-specific defaults
	const typeDefaults = def.defaults ?? {};

    // Build new node object
	const next = {
		...base,
		...typeDefaults,
		key,
		type: def.key,
		userKey: user.key,
		user: buildUserSummary(user),
	};

	applySchemaFieldDefaults(next, def.key);

	// Set workspaceKey if provided
	if (typeof workspaceKey === 'string' && workspaceKey.trim()) {
		next.workspaceKey = workspaceKey.trim();
	}

    // Return the newly created node object
	return next;
}

/**
 * Builds a summary object for a single node.
 *
 * @param {object} item - The node to summarize.
	* @param {string|null} anchorNodeKey - The anchor node key (optional).
 * @param {object|null} user - The user creating the summary (optional).
 *
 * @returns {object|null} The summary object, or null if invalid input.
 */
export const buildSummary = (item, anchorNodeKey = null, user = null) => {
	if (!item || typeof item !== 'object') return null;

	const resolvedUserKey = user?.key || item.userKey || null;
	const resolvedUser = user
		? buildUserSummary(user)
		: (item.user ? buildUserSummary(item.user) : (item.user || null));

	return {
		key: item.key || null,
		userKey: resolvedUserKey,
		user: resolvedUser,
		type: item.type || null,
		title: item.title || '',
		coverUrl: item.coverUrl || null,
		data_file: item.data_file || {  },
		data_document: item.data_document || {  },
		data_photo: item.data_photo || null,
		url: item.url || null,
		url_feed: item.url_feed || null,
		url_grid: item.url_grid || null,
		url_thumb: item.url_thumb || null,
		storagePath: item.storagePath || null,
		targetKey: item.targetKey || null,
		targetType: item.targetType || null,
		targetNode: item.targetNode || null,
		dateCreated: item.dateCreated || null,
		dateModified: item.dateModified || null,
	};
};

/**
 * Builds summaries for uploaded items to be stored in the parent node.
 * 
 * @param {object[]} uploadedItems - Array of items that were uploaded.
	* @param {string} anchorNodeKey - The key of the anchor node.
 * @param {object} user - The user object for the node creator.
 * 
 * @returns {Array} - Array of summary objects for each uploaded item.
 */
export const buildSummaries = (items, anchorNodeKey, user) => {
	if (!Array.isArray(items) || items.length === 0) return [];
	return items
		.map((item) => buildSummary(item, anchorNodeKey, user))
		.filter(Boolean);
};

// ------ FILE NODE UTILITIES ------ //

/**
 * buildChildNodesFromFiles
 * 
 * Builds child node objects from an array of file objects.
 * 
 * @param {array} files - The array of file objects.
 * @param {object} options - Options for building child nodes.
 *   - parentNode: The parent node wrapper.
 *   - parentPermissions: The permissions to assign to the child nodes.
 *   - user: The user creating the nodes.
 *   - now: The current timestamp.
 * 
 * @returns {array} - An array of constructed child node objects.
 */
export const buildChildNodesFromFiles = (files, { parentNode, parentPermissions, user, now } = {}) => {

    // Validate inputs
    if (!Array.isArray(files) || files.length === 0) return [];
    if (!user?.key) return [];
	const anchorNodeKey = parentNode?.key || parentNode?.node?.key || null;
	if (!anchorNodeKey) return [];

    // Build and return child nodes from files
    return files.map((file) => {
        const type = detectFileNodeType(file?.type);

        let title = null;
        let data_document = null;

        // Handle DOCUMENT type nodes
        if (type === 'DOCUMENT' && file?.name) {
            // Extract filename without extension for DOCUMENT nodes
            title = file.name.replace(/\.[^/.]+$/, '');

            // Store document metadata
            data_document = {
                filename: file.name,
                size: file.size,
                mimeType: file.type,
                lastModified: file.lastModified || null,
                extension: file.name.split('.').pop()?.toLowerCase() || '',
            };
        }

		// Return the constructed child node object
        return {
            key: generateKey(),
            type,
            title,
            data_document,
            userKey: user.key,
            user: buildUserSummary(user),
            url: file?.uri,
            base64Data: file?.base64Data || null,
            localUri: file?.localUri || null,
            dateCreated: now,
            dateModified: now,
            permissions: parentPermissions ? JSON.parse(JSON.stringify(parentPermissions)) : null,
        };
    });
};

/**
 * cloneCoverDrafts
 * 
 * Deep clones cover drafts, handling File and Blob objects appropriately.
 * 
 * @param {object} drafts - The cover drafts to clone.
 * 
 * @returns {object} - The cloned cover drafts.
 */
export const cloneCoverDrafts = (drafts) => {

    // Handle null or invalid drafts
    if (!drafts || typeof drafts !== 'object') return {};

    const clone = {};

    // Deep clone each draft field
    Object.entries(drafts).forEach(([key, value]) => {
        if (Array.isArray(value)) {
            clone[key] = value.map((item) => {
                if (!item || typeof item !== 'object') return item;
                if ((typeof File !== 'undefined' && item instanceof File) || (typeof Blob !== 'undefined' && item instanceof Blob)) {
                    return item;
                }
                return { ...item };
            });
        } else if (value && typeof value === 'object') {
            if ((typeof File !== 'undefined' && value instanceof File) || (typeof Blob !== 'undefined' && value instanceof Blob)) {
                clone[key] = value;
            } else {
                clone[key] = { ...value };
            }
        } else {
            clone[key] = value;
        }
    });
    return clone;
};

/**
 * cloneRemovedNodes
 * 
 * Deep clones an array of removed nodes.
 * 
 * @param {array} nodes - The removed nodes to clone.
 * 
 * @returns {array} - The cloned removed nodes.
 */
export const cloneRemovedNodes = (nodes) => (
    Array.isArray(nodes)
        ? nodes.map((node) => (node && typeof node === 'object' ? { ...node } : node))
        : []
);
